/**
 * NodeConfigModal - Modal for configuring a node
 * Molecule component (uses atoms: TextField)
 */
import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import Editor from '@monaco-editor/react';
import { IonIcon } from '@ionic/react';
import { useNavigate } from 'react-router-dom';
import {
  chevronUpOutline, chevronDownOutline, expandOutline, contractOutline,
  chevronExpandOutline, chevronCollapseOutline,
} from 'ionicons/icons';
import { VariableInspector } from './VariableInspector';
import { getAvailableStateVariables, getGroupedStateVariables } from '../utils/stateVariables';
import PillButton from '../../shared/PillButton';
import { useAgent } from '../../../hooks/useAgents';
import './NodeConfigModal.css';

// The read-only graph view used to EMBED a sub-workflow node's CHILD agent
// graph inside this modal. Lazy-loaded to break the import cycle
// (PipelineGraphView → GraphFlowEditor → NodeConfigModal → PipelineGraphView).
const ChildGraphView = lazy(() => import('../../shared/PipelineGraphView/PipelineGraphView'));

const AVAILABLE_TOOLS = [
  {
    id: 'jira',
    label: 'Update Jira',
    description: 'Read/update ticket fields, add comments, transition status',
    capabilities: [
      { name: 'jira_search', description: 'Search issues using JQL queries' },
      { name: 'jira_get_issue', description: 'Get full issue details, description, comments' },
      { name: 'jira_add_comment', description: 'Add a comment to a Jira issue' },
      { name: 'jira_edit_issue', description: 'Update fields (summary, story points, labels, priority)' },
      { name: 'jira_transition_issue', description: 'Move issue to a different status' }
    ]
  },
  {
    id: 'slack_notify',
    label: 'Slack Notify',
    description: 'Send messages to Slack channels, DMs, and threads',
    capabilities: [
      { name: 'slack_list_channels', description: 'List public channels in the workspace' },
      { name: 'slack_post_message', description: 'Post a message to a channel or DM' },
      { name: 'slack_reply_to_thread', description: 'Reply to a specific message thread' },
      { name: 'slack_add_reaction', description: 'Add an emoji reaction to a message' },
      { name: 'slack_get_channel_history', description: 'Get recent messages from a channel' },
      { name: 'slack_get_thread_replies', description: 'Get all replies in a message thread' },
      { name: 'slack_get_users', description: 'List workspace users with basic profiles' },
      { name: 'slack_get_user_profile', description: 'Get detailed profile for a specific user' },
    ]
  },
  {
    id: 'github',
    label: 'GitHub API',
    description: 'Create issues, add comments, manage labels and reviews'
  }
];

// Decide whether a node is an AGENT / LLM node — i.e. one whose prompt actually
// flows through `invokeAgent`, which is the ONLY place a saved Custom prompt
// (`extraPromptInstructions`) is read (engine: strategy-registry "PRIORITY
// OVERRIDE" block). For PURE-CODE / deterministic nodes (open-PR, notify,
// fetch-issue, …) the custom prompt is never read, so the field is inert and
// must be hidden.
//
// Authoritative signal from the serialized node config (agent-workflow
// graph.serialize()):
//   • a declarative or function `prompt`  → agent node (`config.prompt`, with
//     `promptIsCode` for the function case); OR
//   • an `execute`/source body that calls `invokeAgent` itself (e.g. the
//     sentry-triage `fix` node — no top-level prompt, but its execute does
//     `invokeAgent(buildFixPrompt(...), { state })`, so the override DOES apply).
// A node with NEITHER a prompt NOR an `invokeAgent` call is pure code → NOT an
// agent node. (The previous heuristic defaulted missing-code to "AI", which
// wrongly showed the field on code nodes whose source the backend didn't ship.)
function detectIsAINode(nodeConfig, code) {
  if (nodeConfig && nodeConfig.prompt) return true;
  if (!code) return false;
  try {
    return code.includes('invokeAgent');
  } catch {
    return false;
  }
}

const VariableDetailPanel = ({ path, schema, onClose, onInsert, focusedEditor }) => {
  if (!path || !schema) return null;

  const formatValue = (val) => {
    if (val === undefined || val === null) return 'null';
    if (typeof val === 'string') return `"${val}"`;
    if (typeof val === 'object') return JSON.stringify(val, null, 2);
    return String(val);
  };

  // Show different syntax based on which editor pane is focused
  const isPromptTab = focusedEditor === 'prompt';
  const displayPath = isPromptTab ? `{{${path}}}` : `state.get('${path}')`;
  const buttonLabel = isPromptTab ? 'Insert {{}}' : 'Insert';
  const syntaxHint = isPromptTab 
    ? 'Add to promptValues in execute.js to use this variable'
    : 'Access via state.getAll() or state.get()';
  
  return (
    <div className="gwe-variable-detail-panel">
      <button 
        className="gwe-variable-detail__insert-btn"
        onClick={() => onInsert(`@${path}`)}
        title="Insert variable"
      >
        {buttonLabel}
      </button>
      <button className="gwe-variable-detail__close" onClick={onClose}>×</button>
      <div className="gwe-variable-detail__content">
        <code className="gwe-variable-detail__path">{displayPath}</code>
        <div className="gwe-variable-detail__hint">{syntaxHint}</div>
        <div className="gwe-variable-detail__row">
          <span className="gwe-variable-detail__label">Type:</span>
          <span className="gwe-variable-detail__value">{schema.type}</span>
        </div>
        {schema.description && (
          <div className="gwe-variable-detail__row">
            <span className="gwe-variable-detail__label">Description:</span>
            <span className="gwe-variable-detail__value">{schema.description}</span>
          </div>
        )}
        {schema.example !== undefined && (
          <div className="gwe-variable-detail__row gwe-variable-detail__row--full">
            <span className="gwe-variable-detail__label">Example Value:</span>
            <pre className="gwe-variable-detail__example">{formatValue(schema.example)}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

export const NodeConfigModal = ({
  isOpen,
  onClose,
  nodeData,
  nodeConfig = {},
  nodeConfigs = {},
  onSave,
  readOnly = false,
  nodes = [],
  edges = [],
  selectedNodeId = null,
  stateSchema = null,
  // Project scope (optional). Needed only by the sub-workflow embed: a dispatch
  // node's CHILD agent is deployed in the SAME project, so we fetch its graph
  // via the project workflows detail GET. Absent → static placeholder panel.
  projectId = null
}) => {
  // Default prompt from node definition (read-only or editable base)
  const [promptTemplate, setPromptTemplate] = useState(nodeConfig.prompt || '');
  // Node config is view-only everywhere — no edits, no Save. Every editor is
  // locked; the one editable thing is the custom-prompt section below.
  const promptReadOnly = true;
  // Extra user instructions — injected at runtime with PRIORITY over the
  // node's built-in prompt (engine: strategy-registry "PRIORITY OVERRIDE"
  // block). This is the ONE editable thing on a node: the code prompt stays
  // read-only; users add/override behaviour here without touching code.
  const [extraPromptInstructions, setExtraPromptInstructions] = useState(nodeConfig.extraPromptInstructions || '');
  // Custom-prompt section open by default when it already has content.
  const [customPromptOpen, setCustomPromptOpen] = useState(true); // default ALWAYS open
  // Grown: the textarea takes 40% of the modal. `resize: vertical` exists but a
  // drag is a poor fit for the one gesture people repeat — writing a long prompt.
  const [customPromptTall, setCustomPromptTall] = useState(false);
  const [customPromptSaved, setCustomPromptSaved] = useState(false);
  const [enabledTools, setEnabledTools] = useState(nodeConfig.tools || []);
  const [expandedTools, setExpandedTools] = useState({});
  // Execute code — prefer the node's FULL source file (sourceCode) when the
  // backend shipped it (shows helpers, schemas, dynamic prompt builders — the
  // whole file), else fall back to just the execute function for older / not-
  // yet-resynced nodes. Read-only either way.
  const hasSourceCode = typeof nodeConfig.sourceCode === 'string' && nodeConfig.sourceCode.trim() !== '';
  const [executeCode, setExecuteCode] = useState(
    hasSourceCode ? nodeConfig.sourceCode : (nodeConfig.executeCode || nodeConfig.customCode || '')
  );
  // Pane title: the real filename when we're showing the full source, else the
  // historic "execute.js" label for the fallback (execute-function-only) view.
  const codePaneTitle = hasSourceCode ? (nodeConfig.sourceFile || 'source.js') : 'execute.js';
  // activeTab toggles between the default 'split' view (prompt.md | execute.js
  // side by side) and the secondary 'tools' grid (only present for AI nodes).
  const [activeTab, setActiveTab] = useState('split');
  // Enlarge the modal to ~80% of the viewport (not fullscreen) for roomier
  // code reading. Sticky across opens within the session. Monaco's
  // automaticLayout reflows the editors when the box grows.
  const [expanded, setExpanded] = useState(false);
  // Which pane (prompt vs code) the user last focused — drives variable-insert
  // target + the detail-panel insert syntax now that both editors are visible.
  const [focusedEditor, setFocusedEditor] = useState('prompt');
  const [selectedVariable, setSelectedVariable] = useState(null);
  const nodeConfigRef = useRef(nodeConfig);
  nodeConfigRef.current = nodeConfig;
  const prevOpenRef = useRef(false);
  const promptEditorRef = useRef(null);
  const codeEditorRef = useRef(null);
  
  const isAINode = detectIsAINode(nodeConfig, executeCode || null);

  // ── Sub-workflow embed ──────────────────────────────────────────────
  // A dispatch node runs a whole CHILD agent. Because the child is deployed in
  // the SAME project, fetch it (same GET the agent detail page uses — the
  // response carries `graph`) and embed its actual graph read-only. Dangling
  // slug (not deployed), fetch error, or no projectId → the static placeholder
  // panel below. Never recursed: the embed renders with node clicks disabled.
  //
  // DELIBERATELY the SINGLE-child shape only. The marker is a string OR an
  // array of strings, and this panel is a one-child embed: it fetches ONE row
  // and speaks in the singular throughout ("the X agent", "Open agent →").
  // A ROSTER node (one node fanning out over N declared members) has no single
  // child graph to show, so it stays null here and the modal falls through to
  // the normal prompt/code panes — which is the honest view of that node: the
  // fan-out lives in its own execute code, and the code pane shows it. Do NOT
  // "fix" this by picking the first member; that would embed one member's graph
  // under a node that dispatches six.
  const dispatchSlug = typeof nodeConfig.dispatchesWorkflow === 'string' && nodeConfig.dispatchesWorkflow
    ? nodeConfig.dispatchesWorkflow
    : null;
  const navigate = useNavigate();
  const { data: childAgent, isLoading: childLoading } = useAgent(projectId, dispatchSlug, {
    enabled: !!(isOpen && projectId && dispatchSlug),
    retry: false,
  });
  const childGraph = (childAgent && !childAgent.notInstalled
    && childAgent.graph && Array.isArray(childAgent.graph.nodes) && childAgent.graph.nodes.length > 0)
    ? childAgent.graph
    : null;
  const openChildAgent = useCallback(() => {
    if (!projectId || !dispatchSlug) return;
    onClose();
    navigate(`/projects/${projectId}/agents/${encodeURIComponent(dispatchSlug)}`);
  }, [projectId, dispatchSlug, navigate, onClose]);

  const availableVariables = useMemo(() => {
    if (!selectedNodeId || nodes.length === 0) return {};
    return getAvailableStateVariables(selectedNodeId, nodes, edges, stateSchema, nodeConfigs);
  }, [selectedNodeId, nodes, edges, stateSchema, nodeConfigs]);
  
  const variableGroups = useMemo(() => {
    if (!selectedNodeId || nodes.length === 0) return [];
    return getGroupedStateVariables(selectedNodeId, nodes, edges, stateSchema, nodeConfigs);
  }, [selectedNodeId, nodes, edges, stateSchema, nodeConfigs]);
  
  const handleInsertVariable = useCallback((varRef) => {
    const editor = focusedEditor === 'prompt' ? promptEditorRef.current : codeEditorRef.current;
    if (!editor) return;

    // For the prompt pane, use Handlebars syntax {{var}} instead of @var
    let textToInsert = varRef;
    if (focusedEditor === 'prompt') {
      const varName = varRef.startsWith('@') ? varRef.slice(1) : varRef;
      textToInsert = `{{${varName}}}`;
    }

    // Insert at cursor position using Monaco API
    const selection = editor.getSelection();
    const id = { major: 1, minor: 1 };
    const op = { identifier: id, range: selection, text: textToInsert, forceMoveMarkers: true };
    editor.executeEdits('insert-variable', [op]);
    editor.focus();
  }, [focusedEditor]);
  
  const handleSelectVariable = useCallback((path, schema) => {
    setSelectedVariable({ path, schema });
  }, []);
  
  const handleCloseVariableDetail = useCallback(() => {
    setSelectedVariable(null);
  }, []);

  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      const cfg = nodeConfigRef.current;
      setPromptTemplate(cfg.prompt || '');
      setExtraPromptInstructions(cfg.extraPromptInstructions || '');
      setCustomPromptOpen(true); // default ALWAYS open on (re)load
      setCustomPromptSaved(false);
      setEnabledTools(cfg.tools || []);
      setExpandedTools({});
      setExecuteCode(
        (typeof cfg.sourceCode === 'string' && cfg.sourceCode.trim() !== '')
          ? cfg.sourceCode
          : (cfg.executeCode || cfg.customCode || '')
      );
      setActiveTab('split');
      setFocusedEditor('prompt');
      setSelectedVariable(null);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const toggleTool = (toolId) => {
    setEnabledTools(prev =>
      prev.includes(toolId)
        ? prev.filter(id => id !== toolId)
        : [...prev, toolId]
    );
  };
  
  const monacoRef = useRef(null);
  const decorationsRef = useRef([]);

  // Clear markers and decorations when errors are fixed
  const clearMarkers = (editor, owner) => {
    if (editor && monacoRef.current) {
      const model = editor.getModel();
      if (model) {
        monacoRef.current.editor.setModelMarkers(model, owner, []);
      }
    }
    // Also clear inline ghost decorations
    if (editor && decorationsRef.current.length > 0) {
      decorationsRef.current = editor.deltaDecorations(decorationsRef.current, []);
    }
  };


  // Save ONLY the custom prompt (extraPromptInstructions). Everything else is
  // read-only, so we pass the node's config unchanged except this one field —
  // PipelineGraphView's overrideDelta then persists just extraPromptInstructions
  // onto the deployed workflow's nodeConfigOverrides (live; no redeploy). Empty
  // → null clears the override (node falls back to its built-in prompt only).
  const handleSaveCustomPrompt = () => {
    if (!onSave) return;
    onSave({
      ...nodeConfig,
      extraPromptInstructions: extraPromptInstructions.trim() || null,
    });
    setCustomPromptSaved(true);
    setTimeout(() => setCustomPromptSaved(false), 2500);
  };

  if (!isOpen) return null;
  
  return (
    <>
      <div className="gwe-modal-backdrop" onClick={onClose} />
      <div className={`gwe-modal${expanded ? ' gwe-modal--expanded' : ''}`}>
        {/* Title Bar */}
        <div className="gwe-modal__header">
          <button
            type="button"
            className="gwe-modal__enlarge"
            onClick={() => setExpanded((e) => !e)}
            title={expanded ? 'Shrink window' : 'Enlarge window (~80%)'}
            aria-label={expanded ? 'Shrink window' : 'Enlarge window'}
          >
            <IonIcon icon={expanded ? contractOutline : expandOutline} />
          </button>
          <h2 className="gwe-modal__title">{nodeData?.label || 'Node'} — Configuration</h2>
          <div className="gwe-modal__actions">
            {/* Read-only badge + info-tip sit on the RIGHT, just before Close.
                Node config is view-only (no Save) — edit via download + Zibby MCP. */}
            <div className="gwe-prompt-status">
              <span className="gwe-prompt-status__badge gwe-prompt-status__badge--ro">
                READ-ONLY
              </span>
              <span className="gwe-info-tip" tabIndex={0}>
                <span className="gwe-info-tip__mark" aria-hidden>!</span>
                <span className="gwe-info-tip__pop" role="tooltip">
                  Node config is read-only. To change a node, <strong>download the agent</strong>,
                  edit it with your own coding agent through the <strong>Zibby MCP</strong>, then
                  redeploy.
                  {' '}
                  <a
                    className="gwe-info-tip__link"
                    href="https://docs.zibby.app/cloud/editing-prompts"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Learn more →
                  </a>
                </span>
              </span>
            </div>
            <button className="gwe-modal__action-btn gwe-modal__action-btn--cancel" onClick={onClose}>Close</button>
          </div>
        </div>
        
        <div className="gwe-modal__body">
          <div className="gwe-ide-content">
            <div className="gwe-prompt-section">
              {/* Left Sidebar - Variables */}
              {isAINode && (
                <div className="gwe-prompt-sidebar">
                  <VariableInspector
                    groups={variableGroups}
                    availableVariables={availableVariables}
                    onInsertVariable={handleInsertVariable}
                    onSelectVariable={handleSelectVariable}
                    selectedPath={selectedVariable?.path}
                  />
                </div>
              )}
              
              {/* Main Editor */}
              <div className="gwe-prompt-main">
                {/* Tab bar removed — the split (prompt.md | execute.js) is always
                    shown; the read-only badge lives next to the modal title. */}

                {/* Tab Content */}
                <div className="gwe-editor-content" onKeyDown={(e) => e.stopPropagation()}>
                  {dispatchSlug ? (
                    // Sub-graph dispatch node: it runs a whole CHILD agent, not a
                    // prompt. The child is deployed in the SAME project, so show
                    // the child's ACTUAL graph (read-only, node clicks disabled —
                    // modals never recurse) under a compact explainer + deep link.
                    // While the child fetch is in flight → loading note; dangling
                    // slug / fetch error / no project scope → the placeholder
                    // panel (previous behaviour). Slug is template-driven.
                    childGraph ? (
                      <div className="gwe-subgraph-embed" data-testid="gwe-subgraph-embed">
                        <div className="gwe-subgraph-embed__head">
                          <span className="gwe-subgraph-embed__icon" aria-hidden>⧉</span>
                          <p className="gwe-subgraph-embed__text">
                            This node dispatches the <strong>{dispatchSlug}</strong> agent as a
                            child workflow — its input is mapped from this workflow’s state,
                            and the child agent’s final result flows back here.
                          </p>
                          <button
                            type="button"
                            className="gwe-subgraph-embed__open"
                            onClick={openChildAgent}
                          >
                            Open agent →
                          </button>
                        </div>
                        <div className="gwe-subgraph-embed__graph">
                          <Suspense fallback={<div className="gwe-subgraph-embed__loading">Loading child graph…</div>}>
                            <ChildGraphView graph={childGraph} embedded />
                          </Suspense>
                        </div>
                      </div>
                    ) : (
                      <div className="gwe-subgraph-panel">
                        <div className="gwe-subgraph-panel__icon" aria-hidden>⧉</div>
                        <h3 className="gwe-subgraph-panel__title">Sub-workflow</h3>
                        <p className="gwe-subgraph-panel__body">
                          This node doesn’t run a prompt — it dispatches the{' '}
                          <strong>{dispatchSlug}</strong> agent as a
                          child workflow. Its input is mapped from this workflow’s state,
                          and the child agent’s final result flows back here.
                        </p>
                        {childLoading ? (
                          <p className="gwe-subgraph-panel__hint">Loading the {dispatchSlug} agent’s graph…</p>
                        ) : (
                          <p className="gwe-subgraph-panel__hint">
                            Open the <strong>{dispatchSlug}</strong> agent in
                            the marketplace to see its own steps.
                          </p>
                        )}
                      </div>
                    )
                  ) : activeTab === 'split' && (
                    <div className="gwe-editor-split">
                      {/* LEFT pane — prompt.md */}
                      <div className="gwe-split-pane gwe-tab-code">
                        <div className="gwe-split-pane__header">prompt.md</div>
                        {!promptTemplate ? (
                          <div className="gwe-prompt-empty-note">
                            {nodeConfig.promptIsCode
                              ? <>Prompt is built in code — see the full source (<strong>{codePaneTitle}</strong>).</>
                              : <>This node has no declared prompt template — its prompt is built inside the node’s code (see <strong>{codePaneTitle}</strong>).</>}
                          </div>
                        ) : (
                          <div className="gwe-prompt-editor-wrap">
                            <Editor
                              height="100%"
                              defaultLanguage="markdown"
                              language="markdown"
                              theme="vs-dark"
                              loading={<div className="gwe-editor-loading">Loading editor...</div>}
                              value={promptTemplate}
                              onChange={(value) => { setPromptTemplate(value || ''); clearMarkers(promptEditorRef.current, 'handlebars'); }}
                              beforeMount={(monaco) => { monacoRef.current = monaco; }}
                              onMount={(editor) => {
                                promptEditorRef.current = editor;
                                editor.onDidFocusEditorText(() => setFocusedEditor('prompt'));
                              }}
                              options={{
                                readOnly: promptReadOnly,
                                minimap: { enabled: false },
                                fontSize: 13,
                                lineNumbers: 'on',
                                lineNumbersMinChars: 3,
                                lineDecorationsWidth: 6,
                                glyphMargin: false,
                                wordWrap: 'on',
                                wrappingIndent: 'indent',
                                scrollBeyondLastLine: false,
                                automaticLayout: true,
                                tabSize: 2,
                                folding: true,
                                renderWhitespace: 'selection'
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* RIGHT pane — full source file (sourceCode) when shipped,
                          else the execute-function-only fallback. */}
                      <div className="gwe-split-pane gwe-tab-code">
                        <div className="gwe-split-pane__header">{codePaneTitle}</div>
                        {!executeCode ? (
                          <div className="gwe-prompt-empty-note">
                            No custom code — this is a pure prompt/LLM step.
                          </div>
                        ) : (
                          <div className="gwe-prompt-editor-wrap">
                            <Editor
                              height="100%"
                              defaultLanguage="javascript"
                              language="javascript"
                              theme="vs-dark"
                              loading={<div className="gwe-editor-loading">Loading editor...</div>}
                              value={executeCode}
                              onChange={(value) => { setExecuteCode(value || ''); }}
                              options={{
                                readOnly: true,
                                minimap: { enabled: false },
                                fontSize: 13,
                                lineNumbers: 'on',
                                // Tighten the gutter — default lineNumbersMinChars
                                // (5) + decoration width + glyph margin made it
                                // way too wide.
                                lineNumbersMinChars: 3,
                                lineDecorationsWidth: 6,
                                glyphMargin: false,
                                scrollBeyondLastLine: false,
                                automaticLayout: true,
                                tabSize: 2,
                                // Word-wrap OFF on the code pane: every line keeps
                                // its TRUE indentation (no col-0 wrap fallback that
                                // made the code look un-indented). Long lines (the
                                // giant RANK_PROMPT string) scroll horizontally.
                                wordWrap: 'off',
                                // Vertical indent guides + active-nesting highlight
                                // make the nesting unmistakable.
                                guides: { indentation: true, highlightActiveIndentation: true, bracketPairs: true },
                                suggestOnTriggerCharacters: true,
                                quickSuggestions: true,
                                parameterHints: { enabled: true },
                                folding: true,
                                bracketPairColorization: { enabled: true },
                                formatOnPaste: true,
                                formatOnType: true,
                              }}
                              beforeMount={(monaco) => {
                                // Disable semantic validation - this is a code snippet, not a full module
                                // Keeps syntax highlighting but removes "Cannot find name" errors
                                monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
                                  noSemanticValidation: true,
                                  noSyntaxValidation: false,
                                });
                                monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
                                  target: monaco.languages.typescript.ScriptTarget.ESNext,
                                  allowNonTsExtensions: true,
                                  moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
                                  allowJs: true,
                                  checkJs: false,
                                });
                                // Add type definitions for context object (for autocomplete)
                                monaco.languages.typescript.javascriptDefaults.addExtraLib(`
                                  interface WorkflowState {
                                    getAll(): Record<string, any>;
                                    get(key: string): any;
                                    set(key: string, value: any): void;
                                  }
                                  interface InvokeAgentOptions {
                                    model?: string;
                                    schema?: any;
                                    images?: Array<{ mimeType: string; base64: string; filename?: string }>;
                                    tools?: string[];
                                  }
                                  interface NodeContext {
                                    state: WorkflowState;
                                    invokeAgent: (promptValues: Record<string, any>, options?: InvokeAgentOptions) => Promise<any>;
                                    nodeId: string;
                                    promptTemplate: string;
                                    getPromptTemplate: () => string;
                                  }
                                  declare const context: NodeContext;
                                  declare const console: Console;
                                `, 'ts:context.d.ts');
                              }}
                              onMount={(editor) => {
                                codeEditorRef.current = editor;
                                editor.onDidFocusEditorText(() => setFocusedEditor('code'));
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}


                  {activeTab === 'tools' && isAINode && (
                    <div className="gwe-tab-tools">
                      <div className="gwe-tools-header">
                        <span>Select tools this node can use during execution</span>
                      </div>
                      <div className="gwe-tools-grid">
                        {AVAILABLE_TOOLS.map(tool => {
                          const active = enabledTools.includes(tool.id);
                          const expanded = expandedTools[tool.id];
                          return (
                            <div key={tool.id} className={`gwe-tool-card-wrapper ${active && expanded && tool.capabilities ? 'gwe-tool-card-wrapper--expanded' : ''}`}>
                              <div
                                className={`gwe-tool-card ${active ? 'gwe-tool-card--active' : ''}`}
                              >
                                <div
                                  className="gwe-tool-card__header"
                                  onClick={() => {
                                    if (active && tool.capabilities) {
                                      setExpandedTools(prev => ({ ...prev, [tool.id]: !prev[tool.id] }));
                                    }
                                  }}
                                  style={{ cursor: active && tool.capabilities ? 'pointer' : 'default' }}
                                >
                                  <span className="gwe-tool-card__name">{tool.label}</span>
                                  <span
                                    className={`gwe-tool-card__toggle ${active ? 'gwe-tool-card__toggle--on' : ''}`}
                                    onClick={(e) => { e.stopPropagation(); toggleTool(tool.id); }}
                                    role="switch"
                                    aria-checked={active}
                                  />
                                </div>
                                <span
                                  className="gwe-tool-card__desc"
                                  onClick={() => {
                                    if (active && tool.capabilities) {
                                      setExpandedTools(prev => ({ ...prev, [tool.id]: !prev[tool.id] }));
                                    }
                                  }}
                                  style={{ cursor: active && tool.capabilities ? 'pointer' : 'default' }}
                                >{tool.description}</span>
                              </div>
                              {active && expanded && tool.capabilities && (
                                <div className="gwe-tool-capabilities">
                                  <span className="gwe-tool-capabilities__title">Available MCP tools:</span>
                                  <ul className="gwe-tool-capabilities__list">
                                    {tool.capabilities.map(cap => (
                                      <li key={cap.name} className="gwe-tool-capabilities__item">
                                        <code className="gwe-tool-capabilities__name">{cap.name}</code>
                                        <span className="gwe-tool-capabilities__desc">{cap.description}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                </div>

                {/* Variable Detail Panel - Below Editor */}
                {selectedVariable && (
                  <VariableDetailPanel
                    path={selectedVariable.path}
                    schema={selectedVariable.schema}
                    onClose={handleCloseVariableDetail}
                    onInsert={handleInsertVariable}
                    focusedEditor={focusedEditor}
                  />
                )}
                {/* Custom prompt — moved INTO the main column so the Variables
                    sidebar stays full height and this sits at the bottom-RIGHT
                    (not a full-width row under the sidebar). The ONE editable
                    thing: extra instructions injected at runtime with PRIORITY
                    over the node's built-in (read-only) prompt. */}
                {isAINode && !readOnly && onSave && (
          <div className={`gwe-custom-prompt${customPromptOpen ? ' is-open' : ''}${customPromptTall ? ' is-tall' : ''}`}>
            {/* The label row holds TWO controls, so it is a row of buttons rather
                than one button: nesting the grow control inside the collapse
                button would be invalid markup and would swallow its clicks. */}
            <div className="gwe-custom-prompt__head">
              <button
                type="button"
                className="gwe-custom-prompt__toggle"
                onClick={() => setCustomPromptOpen((o) => !o)}
                aria-expanded={customPromptOpen}
              >
                <span className="gwe-custom-prompt__title">
                  Custom prompt
                </span>
                <IonIcon icon={customPromptOpen ? chevronUpOutline : chevronDownOutline} aria-hidden />
              </button>
              {/* Rendered in BOTH states so the collapse chevron never shifts
                  sideways when you open the section. Growing while collapsed
                  opens it — the alternative is a control that visibly does
                  nothing. */}
              <button
                type="button"
                className="gwe-custom-prompt__grow"
                onClick={() => { setCustomPromptOpen(true); setCustomPromptTall((t) => !t); }}
                aria-pressed={customPromptTall}
                title={customPromptTall ? 'Shrink the box' : 'Expand the box'}
                aria-label={customPromptTall ? 'Shrink the custom-prompt box' : 'Expand the custom-prompt box'}
              >
                <IonIcon icon={customPromptTall ? chevronCollapseOutline : chevronExpandOutline} aria-hidden />
              </button>
            </div>
            {customPromptOpen && (
              <div className="gwe-custom-prompt__body">
                <textarea
                  className="gwe-custom-prompt__input"
                  value={extraPromptInstructions}
                  onChange={(e) => { setExtraPromptInstructions(e.target.value); setCustomPromptSaved(false); }}
                  placeholder="e.g. Always answer in English. Keep replies under 3 sentences. Never approve a PR that touches billing without tests."
                  rows={4}
                  spellCheck={false}
                />
                {/* Caption position, deliberate: the box is what you came for, so
                    the explainer reads AFTER it rather than standing between the
                    label and the control it describes. */}
                <p className="gwe-custom-prompt__hint">
                  Extra instructions injected at runtime with <strong>priority</strong> over this
                  node’s built-in prompt — applied only when filled. Saved live; no redeploy.
                </p>
                <div className="gwe-custom-prompt__actions">
                  {customPromptSaved && <span className="gwe-custom-prompt__saved">✓ Saved</span>}
                  <PillButton className="pill-button--sm" onClick={handleSaveCustomPrompt}>
                    Save custom prompt
                  </PillButton>
                </div>
              </div>
            )}
          </div>
        )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
};
